/**
 * Lazy-Zugriff auf Sentry.
 *
 * `main.tsx` und `Dashboard.tsx` laden Sentry bereits dynamisch — ein einziger
 * statischer `import * as Sentry` (bisher in `useAuth` und `ErrorBoundary`) zog
 * die Bibliothek (~173 kB Quellcode) aber trotzdem in den Boot-Chunk. Diese
 * Helfer kapseln den dynamischen Import an einer Stelle.
 *
 * Monitoring darf den App-Start nie blockieren oder Fehler verschlucken, ohne
 * dass man es merkt: Schlägt der Import fehl (z. B. Adblocker), wird genau
 * einmal gewarnt.
 */

type SentryModule = typeof import("@sentry/react");

let sentryPromise: Promise<SentryModule> | null = null;
let loadFailureLogged = false;

function loadSentry(): Promise<SentryModule> {
  if (!sentryPromise) {
    sentryPromise = import("@sentry/react");
  }
  return sentryPromise;
}

function onLoadFailure(error: unknown): void {
  if (loadFailureLogged) return;
  loadFailureLogged = true;
  console.warn("Sentry konnte nicht geladen werden (Monitoring inaktiv):", error);
}

/** Setzt bzw. entfernt die Sentry-User-ID (keine weiteren personenbezogenen Daten). */
export function setSentryUser(user: { id: string } | null): void {
  void loadSentry()
    .then(Sentry => Sentry.setUser(user))
    .catch(onLoadFailure);
}

/** Meldet einen Fehler an Sentry. */
export function captureSentryException(
  error: unknown,
  extra?: Record<string, unknown>
): void {
  void loadSentry()
    .then(Sentry =>
      Sentry.captureException(error, extra ? { extra } : undefined)
    )
    .catch(onLoadFailure);
}
